import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class RecommendationProductDrawer extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('data-cart-preview')) {
      // Stylesheet and script tags sit between the cart and its preview.
      let sibling = this.previousElementSibling;
      while (sibling && !sibling.matches('cart-drawer-component')) {
        sibling = sibling.previousElementSibling;
      }
      this.section = sibling;
    } else {
      this.section = this.closest('product-recommendations, [data-mlf-other-collections]');
    }
    this.collectionPreview = this.section?.hasAttribute('data-mlf-other-collections') === true;
    if (this.collectionPreview) this.section.setAttribute('data-collection-product-preview-ready', '');
    this.section?.addEventListener('click', this.openProduct, this.collectionPreview);
    this.addEventListener('change', this.updateVariant);
    this.addEventListener('submit', this.addProduct);
    this.addEventListener('dialog:close', this.restoreCart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener('click', this.openProduct, this.collectionPreview);
    if (this.collectionPreview) this.section?.removeAttribute('data-collection-product-preview-ready');
    this.removeEventListener('change', this.updateVariant);
    this.removeEventListener('submit', this.addProduct);
    this.removeEventListener('dialog:close', this.restoreCart);
  }

  openProduct = async (event) => {
    if (!(event.target instanceof Element)) return;
    const collectionCard = this.collectionPreview ? event.target.closest('.product-grid__item') : null;
    const control = event.target.closest('button, input, label, select, summary, a');
    if (collectionCard && control && !control.matches('.product-card__link')) return;
    const link = collectionCard?.querySelector('.product-card__link') || event.target.closest('.recommendation-card__link, .cart-drawer__recently-viewed-card');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const template = (collectionCard || link.closest('.recommendation-card') || link).querySelector('template[data-recommendation-preview]');
    if (!template || this.busy) return;
    event.preventDefault();
    if (collectionCard) event.stopPropagation();
    this.querySelector('[data-preview-content]').replaceChildren(template.content.cloneNode(true));
    this.image = this.querySelector('.recommendation-preview__image');
    this.defaultImage = this.image?.getAttribute('src');
    this.updateVariant();
    this.originCart = this.section?.matches('cart-drawer-component') ? this.section : null;
    this.returnToCart = Boolean(this.originCart);
    if (this.originCart) await this.originCart.closeDialog();
    if (this.isConnected) this.showDialog();
  };

  restoreCart = () => {
    if (this.returnToCart && this.originCart?.isConnected) {
      this.returnToCart = false;
      this.originCart.open(this.originCart.dataset.cartMode || 'full');
    }
  };

  updateVariant = () => {
    const select = this.querySelector('[data-preview-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const button = this.querySelector('[data-preview-add]');
    button.disabled = this.busy || !select.value || option.disabled || button.hasAttribute('data-requires-selling-plan');
    const price = this.querySelector('[data-preview-price]');
    price.textContent = option.dataset.price || select.querySelector('option[data-price]')?.dataset.price || '';
    this.querySelector('[name="quantity"]').value = option.dataset.quantity || '1';
    if (this.image) {
      // A responsive srcset would otherwise keep showing the original product image.
      this.image.removeAttribute('srcset');
      this.image.setAttribute('src', option.dataset.image || this.defaultImage);
    }
  };

  addProduct = async (event) => {
    const form = event.target.closest('[data-preview-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[data-preview-add]');
    if (this.busy || button.disabled) return;
    this.busy = true;
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Adding…';
    const error = form.querySelector('[data-preview-error]');
    error.textContent = '';
    const body = new FormData(form);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
    if (sectionIds.length) body.set('sections', sectionIds.join(','));
    body.set('sections_url', window.location.pathname);
    try {
      const response = await fetch(form.action, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (result.cartOnlyBagChoice) {
        this.returnToCart = false;
        await this.closeDialog();
        document.dispatchEvent(new CustomEvent('mlf:bag-choice-rejected'));
        return;
      }
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
      this.returnToCart = false;
      await this.closeDialog();
      this.dispatchEvent(new CartAddEvent({}, body.get('id'), {
        source: 'product-form-component',
        itemCount: Number(body.get('quantity')),
        productId: this.querySelector('[data-product-id]').dataset.productId,
        variantId: body.get('id'),
        sections: result.sections,
      }));
      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open();
      if (!cartDrawer) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (failure) {
      error.textContent = failure.message || 'Unable to add this item. Please try again.';
    } finally {
      this.busy = false;
      button.textContent = originalLabel;
      this.updateVariant();
    }
  };
}

if (!customElements.get('recommendation-product-drawer')) {
  customElements.define('recommendation-product-drawer', RecommendationProductDrawer);
}
